import { Link } from 'react-router-dom';
import PictureImage from '../Common/PictureImage';
import { IMAGE_ALT, imageSizeProps } from '../../seo/imageAltText';
import { customerReviews } from '../../data/customerReviews';
import './MultiChannelTrustSection.css';

const CHANNEL_LOGOS = [
  { name: 'Amazon', src: '/Images/merchent/amazon.webp' },
  { name: 'Shopify', src: '/Images/merchent/shopify.webp' },
  { name: 'eBay', src: '/Images/merchent/ebay.webp' },
  { name: 'TikTok Shop', src: '/Images/merchent/tiktok.webp' },
  { name: 'Etsy', src: '/Images/merchent/etsy.webp' },
  { name: 'Wayfair', src: '/Images/merchent/wayfair.webp' },
  { name: 'BigCommerce', src: '/Images/merchent/bigcommerce.webp' },
  { name: 'WooCommerce', src: '/Images/merchent/woo.webp' },
] as const;

/** Named clients from verified Google reviews on /our-reviews */
const CLIENT_BRANDS = [
  { name: 'Insta Mart', abbr: 'IM' },
  { name: 'Syb Ash · Multichannel', abbr: 'SA' },
  { name: 'Jack · Amazon seller', abbr: 'JK' },
  { name: 'Amanda W. · SFP', abbr: 'AW' },
  { name: 'Vignesh M. · E-Commerce', abbr: 'VM' },
  { name: 'Ibrar A. · Online retail', abbr: 'IA' },
] as const;

const multichannelReview =
  customerReviews.find((review) => review.company === 'Multichannel retail') ?? customerReviews[1];

const instaMartReview = customerReviews.find((review) => review.name === 'Insta Mart');

export default function MultiChannelTrustSection() {
  return (
    <section className="mcf-trust" aria-labelledby="mcf-trust-heading">
      <div className="mcf-trust-inner">
        <header className="mcf-trust-logos-head">
          <p className="mcf-trust-eyebrow">Social proof</p>
          <h2 id="mcf-trust-heading" className="mcf-trust-title">
            Trusted by UK brands selling across multiple channels
          </h2>
          <p className="mcf-trust-lead">
            Pick Pack Pro provides ecommerce fulfilment for marketplace sellers, DTC brands and multichannel retailers from one Milton Keynes fulfilment centre UK — with verified Google reviews you can read before you sign up.
          </p>
        </header>

        <div className="mcf-trust-strip">
        <ul className="mcf-trust-logo-row" aria-label="Sales channels we fulfil for UK brands">
          {CHANNEL_LOGOS.map((logo) => (
            <li key={logo.name} className="mcf-trust-logo-slot">
              <PictureImage
                src={logo.src}
                alt={IMAGE_ALT.homepageMarketplaceLogo(logo.name)}
                className="mcf-trust-logo-img"
                {...imageSizeProps(logo.src)}
                loading="lazy"
              />
            </li>
          ))}
        </ul>

        <ul className="mcf-trust-client-row" aria-label="Client types and brands we fulfil for">
          {CLIENT_BRANDS.map((brand) => (
            <li key={brand.name} className="mcf-trust-client-pill">
              <span className="mcf-trust-client-abbr" aria-hidden="true">
                {brand.abbr}
              </span>
              <span>{brand.name}</span>
            </li>
          ))}
        </ul>

        <div className="mcf-trust-badges" aria-label="Trust and performance highlights">
          <div className="mcf-trust-badge">
            <span className="mcf-trust-badge-value">5.0</span>
            <span className="mcf-trust-badge-label">Google rating</span>
          </div>
          <div className="mcf-trust-badge">
            <span className="mcf-trust-badge-value">250+</span>
            <span className="mcf-trust-badge-label">Clients served</span>
          </div>
          <div className="mcf-trust-badge">
            <span className="mcf-trust-badge-value">99.9%</span>
            <span className="mcf-trust-badge-label">Order accuracy</span>
          </div>
          <div className="mcf-trust-badge">
            <span className="mcf-trust-badge-value">MK</span>
            <span className="mcf-trust-badge-label">UK fulfilment centre</span>
          </div>
        </div>
        </div>

        <div className="mcf-trust-grid">
          <figure className="mcf-trust-quote" aria-label="Client testimonial">
            <div className="mcf-trust-stars" aria-hidden="true">
              {'★'.repeat(multichannelReview.stars)}
            </div>
            <blockquote cite="/our-reviews">
              &ldquo;{multichannelReview.text}&rdquo;
            </blockquote>
            <figcaption>
              <strong>{multichannelReview.name}</strong>
              <span>{multichannelReview.company}</span>
            </figcaption>
            <Link to="/our-reviews" className="mcf-trust-link">
              Read verified Google reviews →
            </Link>
          </figure>

          <article className="mcf-trust-case" aria-labelledby="mcf-trust-case-title">
            <span className="mcf-trust-case-tag">Case study</span>
            <h3 id="mcf-trust-case-title">Insta Mart — multichannel &amp; Seller Fulfilled Prime</h3>
            <p className="mcf-trust-case-metric">
              Scaled daily order volume without hiring in-house warehouse staff.
            </p>
            <p className="mcf-trust-case-body">
              Insta Mart consolidated marketplace, DTC and SFP orders into one Pick Pack Pro workflow.
              {instaMartReview
                ? ` ${instaMartReview.text}`
                : ' Orders are handled reliably with SFP-compliant pick, pack and dispatch from our UK fulfilment centre.'}
            </p>
            <Link to="/case-studies" className="mcf-trust-link">
              See more success stories →
            </Link>
          </article>
        </div>
      </div>
    </section>
  );
}
